import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableBlockToken, useBlockToken } from '@vibesites/runtime/react';

function EngrainSitemapComponent(_props: BlockProps) {
  const sitemapId =
    useBlockToken('sitemap_id', { defaultField: 'custom_engrain_sightmap_id' }) ||
    useBlockToken('sitemap_id', { defaultField: 'custom_engrain_sitemap_id' });
  const sitemapUrl =
    useBlockToken('sitemap_url', { defaultField: 'custom_engrain_sightmap_url' }) ||
    useBlockToken('sitemap_url', { defaultField: 'custom_engrain_sitemap_url' });
  const height = useBlockToken('height', { defaultValue: '1000' });
  const title = useBlockToken('title', { defaultValue: 'Engrain Sightmap' });

  const src = sitemapUrl || (sitemapId ? `https://sightmap.com/embed/${sitemapId}` : null);

  if (!src) {
    return (
      <div
        className="flex items-center justify-center bg-muted px-6 py-16 text-center"
        style={{ color: 'var(--vibe-color-text-muted)' }}
      >
        <div>
          <EditableBlockToken
            localName="sitemap_id"
            type="text"
            label="Engrain Sightmap ID"
            as="p"
            className="text-lg font-medium"
            style={{ fontFamily: 'var(--vibe-font-heading)' }}
          >
            Engrain Sightmap
          </EditableBlockToken>
          <p className="mt-1 text-sm">
            Add an Engrain Sightmap ID to load the interactive map.
          </p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={src}
      title={title || 'Engrain Sightmap'}
      width="100%"
      height={height || '1000'}
      style={{ border: 'none', display: 'block' }}
      loading="lazy"
    />
  );
}

const contentSchema: ComponentContentSchema = {
  slot: 'EngrainSitemap',
  displayName: 'Engrain Sightmap',
  tokens: {
    sitemap_id: {
      label: 'Sightmap ID',
      type: 'text',
      defaultField: 'custom_engrain_sightmap_id',
      suggestedFields: ['custom_engrain_sightmap_id'],
      description: 'Engrain sightmap identifier appended to the embed URL.',
    },
    sitemap_url: {
      label: 'Sightmap URL',
      type: 'text',
      defaultField: 'custom_engrain_sightmap_url',
      suggestedFields: ['custom_engrain_sightmap_url'],
      description:
        'Full Engrain embed URL (e.g. https://sightmap.com/embed/yzvg8lq7pln). Takes precedence over Sightmap ID.',
    },
    height: {
      label: 'Height (px)',
      type: 'text',
      defaultValue: '1000',
      description: 'Iframe height in pixels.',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Engrain Sightmap',
      description: 'Accessible iframe title.',
    },
  },
  contentRules: [
    { kind: 'oneOf', tokens: ['sitemap_id', 'sitemap_url'], message: 'Add a Sightmap ID or a Sightmap URL.' },
  ],
};

export const engrainSitemapBlock: BlockDefinition = {
  id: 'engrain-sitemap',
  name: 'Engrain Sightmap',
  description: 'Embeds an Engrain Sightmap iframe using a per-property sightmap ID.',
  category: 'Embeds',
  component: EngrainSitemapComponent,
  contentSchema,
  requirements: {
    designTokens: ['color.textMuted', 'font.heading'],
    dataTokens: [],
  },
  tags: ['engrain', 'sitemap', 'sightmap', 'iframe', 'map', 'floorplans'],
  defaultGridSpan: 12,
};
